import useLocalStorage from "@rehooks/local-storage";
import { useMemo } from "react";

import useBreadcrumbs from "components/Breadcrumbs/useBreadcrumbs";
import { EmptystateSearchNoResultsColored } from "components/icons/generated";
import PageLoading from "components/loading/PageLoading";
import SearchInput from "components/SearchInput";
import EmptyState from "ds/components/EmptyState";
import PageInfoHeader from "ds/components/PageInfoHeader";
import SegmentedControl from "ds/components/SegmentedControl";
import Select from "ds/components/Select";
import SelectInput from "ds/components/Select/Input";
import Typography from "ds/components/Typography";
import useAnalytics from "hooks/useAnalytics";
import { AnalyticsPageStack } from "hooks/useAnalytics/pages/stack";
import useErrorHandle from "hooks/useErrorHandle";
import useTitle from "hooks/useTitle";
import useTypedContext from "hooks/useTypedContext";
import {
  ANSIBLE_GROUP_BY_OPTIONS,
  LAYOUT_MODE_OPTIONS,
} from "views/Account/ConfigManagement/constants";
import ConfigManagementEmptyState from "views/Account/ConfigManagement/EmptyState";
import ConfigManagementFiltersLayout from "views/Account/ConfigManagement/FiltersLayout";
import ConfigManagementTreeGrid from "views/Account/ConfigManagement/TreeGrid";
import { AnsibleGroupBy, LayoutMode } from "views/Account/ConfigManagement/types";
import useSearchAnsibleNodes from "views/Account/ConfigManagement/useSearchAnsibleNodes";

import StackHeader from "../components/Header";
import { StackContext } from "../Context";
import { getStacksBackUrl } from "../helpers";
import {
  DEFAULT_GROUP_BY_MODE,
  DEFAULT_LAYOUT_MODE,
  FILTERS_ORDER_SETTINGS_KEY,
  GROUP_BY_MODE_LOCAL_STORAGE_KEY,
  HIDDEN_FILTERS,
  LAYOUT_MODE_LOCAL_STORAGE_KEY,
} from "./constants";
import styles from "./styles.module.css";

const StackConfigManagement = () => {
  const [groupBy, setGroupBy] = useLocalStorage<AnsibleGroupBy>(
    GROUP_BY_MODE_LOCAL_STORAGE_KEY,
    DEFAULT_GROUP_BY_MODE
  );

  const [layoutMode, setLayoutMode] = useLocalStorage<LayoutMode>(
    LAYOUT_MODE_LOCAL_STORAGE_KEY,
    DEFAULT_LAYOUT_MODE
  );

  const trackSegmentAnalyticsEvent = useAnalytics({
    page: AnalyticsPageStack.StacksConfigurationManagement,
  });

  const { stack } = useTypedContext(StackContext);

  useTitle(`Configuration management · ${stack.name}`);

  useBreadcrumbs([
    {
      title: "Stacks",
      link: getStacksBackUrl(),
    },
    {
      title: stack.name,
    },
  ]);

  const forcedStackPredicate = useMemo(
    () => ({
      field: "stack",
      exclude: null,
      constraint: {
        booleanEquals: null,
        enumEquals: null,
        stringMatches: [stack.id],
        hierarchyNodeValueEquals: null,
        hierarchyNode: null,
        timeInRange: null,
        timeInLast: null,
      },
    }),
    [stack.id]
  );

  const {
    isPageEmpty,
    predicates,
    nodes,
    hasNoFilteringResults,
    isPageLoading,
    error,
    loadMoreItems,
    hasNextPageToLoad,
  } = useSearchAnsibleNodes(groupBy, layoutMode, forcedStackPredicate);

  const ErrorContent = useErrorHandle(error);

  if (ErrorContent) {
    return ErrorContent;
  }

  return (
    <>
      <StackHeader />
      <PageInfoHeader title="Resources">
        <SearchInput
          placeholder="Search by host or task name"
          filtersOrderSettingsKey={FILTERS_ORDER_SETTINGS_KEY}
        />
        <Select
          value={groupBy}
          items={ANSIBLE_GROUP_BY_OPTIONS}
          onChange={(value) => {
            trackSegmentAnalyticsEvent?.("Group by changed", { groupBy: value });
            setGroupBy(value as AnsibleGroupBy);
          }}
          renderInput={(props) => (
            <SelectInput {...props}>
              <Typography variant="p-t7" tag="span">
                Grouped by: {groupBy}
              </Typography>
            </SelectInput>
          )}
        />
        <SegmentedControl
          optionClassName={styles.layoutSwitchOption}
          onChange={(value) => {
            setLayoutMode(value);
          }}
          value={layoutMode}
          options={LAYOUT_MODE_OPTIONS}
        />
      </PageInfoHeader>

      <ConfigManagementFiltersLayout
        key={groupBy}
        groupBy={groupBy}
        predicates={predicates}
        hiddenFilters={HIDDEN_FILTERS}
        filtersType="stackConfigurationManagement"
      >
        {isPageLoading && <PageLoading />}

        {hasNoFilteringResults && (
          <EmptyState
            title="No results"
            caption="Try different filters."
            icon={EmptystateSearchNoResultsColored}
            announce
          />
        )}

        {!!nodes.length && (
          <ConfigManagementTreeGrid
            hasNextPageToLoad={hasNextPageToLoad}
            loadMoreItems={loadMoreItems}
            nodes={nodes}
            chartClassName={styles.chart}
            layoutMode={layoutMode}
            analyticsPage={AnalyticsPageStack.StacksConfigurationManagement}
          />
        )}

        {isPageEmpty && <ConfigManagementEmptyState />}
      </ConfigManagementFiltersLayout>
    </>
  );
};

export default StackConfigManagement;
